/**
 * Hetzner-style incident strip: full width of the content column, dark
 * ground, a circular severity mark, a bold title, and Created / Last updated.
 *
 * It is not an Alert. Alert is a left-ruled inline note on a page. This sits
 * in the shell so every signed-in route — the server list and a single
 * machine — shows the same thing. Colour is a third channel; the title and
 * `role="status"` carry the meaning on their own.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { StatusNotice, StatusNoticeListResponse, StatusNoticeSeverity } from '../api/types';
import { absoluteTime, parseApiDate, relativeTime } from '../lib/format';
import {
  bannerCopy,
  dismissNotice,
  noticeBody,
  noticeTitle,
  panelLocale,
  visibleNotices,
  type PanelLocale,
} from '../lib/statusNotices';
import { cx } from './ui';

const POLL_MS = 60_000;

const ICON: Record<StatusNoticeSeverity, { fill: string; label: { en: string; fi: string } }> = {
  info: { fill: '#3b82f6', label: { en: 'Information', fi: 'Tiedote' } },
  warning: { fill: '#f59e0b', label: { en: 'Warning', fi: 'Varoitus' } },
  maintenance: { fill: '#f59e0b', label: { en: 'Maintenance', fi: 'Huolto' } },
  outage: { fill: '#ec3013', label: { en: 'Outage', fi: 'Häiriö' } },
};

function SeverityMark({ severity, locale }: { severity: StatusNoticeSeverity; locale: PanelLocale }) {
  const icon = ICON[severity];
  return (
    <span
      className="mt-0.5 flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full"
      style={{ background: icon.fill }}
      title={icon.label[locale]}
    >
      <span className="sr-only">{icon.label[locale]}</span>
      <span className="text-[13px] font-extrabold leading-none text-white" aria-hidden="true">
        !
      </span>
    </span>
  );
}

function formatCreated(value: string, locale: PanelLocale): string {
  const date = parseApiDate(value);
  if (!date) return value;
  try {
    return new Intl.DateTimeFormat(locale === 'fi' ? 'fi-FI' : 'en-GB', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'shortOffset',
    }).format(date);
  } catch {
    return absoluteTime(value);
  }
}

function formatUpdated(value: string, locale: PanelLocale, now: number): string {
  const date = parseApiDate(value);
  if (!date) return '—';
  try {
    const seconds = Math.round((now - date.getTime()) / 1000);
    if (seconds < 45) return locale === 'fi' ? 'juuri äsken' : 'just now';
    const relative = new Intl.RelativeTimeFormat(locale === 'fi' ? 'fi' : 'en', { numeric: 'auto' });
    const units: Array<[Intl.RelativeTimeFormatUnit, number]> = [
      ['year', 31_536_000],
      ['month', 2_592_000],
      ['week', 604_800],
      ['day', 86_400],
      ['hour', 3_600],
      ['minute', 60],
    ];
    for (const [unit, size] of units) {
      if (seconds >= size) return relative.format(-Math.round(seconds / size), unit);
    }
    return locale === 'fi' ? 'juuri äsken' : 'just now';
  } catch {
    return relativeTime(value, now);
  }
}

function BannerRow({
  notice,
  locale,
  now,
  onDismiss,
}: {
  notice: StatusNotice;
  locale: PanelLocale;
  now: number;
  onDismiss: (notice: StatusNotice) => void;
}) {
  const copy = bannerCopy(locale);
  const title = noticeTitle(notice, locale);
  const body = noticeBody(notice, locale);
  const created = formatCreated(notice.created_at, locale);
  const updated = formatUpdated(notice.updated_at, locale, now);

  return (
    <div
      role="status"
      className="flex items-start gap-3.5 px-4 py-3.5 sm:px-6"
    >
      <SeverityMark severity={notice.severity} locale={locale} />
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-bold leading-snug tracking-[-0.015em] text-ink-primary">{title}</p>
        {body ? <p className="mt-0.5 text-[13px] leading-relaxed text-ink-secondary">{body}</p> : null}
        <p className="mt-1 text-[12px] leading-relaxed text-ink-tertiary">
          <span title={absoluteTime(notice.created_at)}>
            {copy.created}: {created}
          </span>
          <span className="px-2.5 text-ink-disabled" aria-hidden="true">
            ·
          </span>
          <span title={absoluteTime(notice.updated_at)}>
            {copy.updated}: {updated}
          </span>
        </p>
      </div>
      <button
        type="button"
        onClick={() => onDismiss(notice)}
        aria-label={copy.dismiss}
        title={copy.dismiss}
        className="tap relative -mr-1 flex h-10 w-10 shrink-0 items-center justify-center text-ink-tertiary transition-colors hover:text-ink-primary"
      >
        <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" />
        </svg>
      </button>
    </div>
  );
}

export default function StatusBanner() {
  const { user } = useAuth();
  const [notices, setNotices] = useState<StatusNotice[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const locale = useMemo(() => panelLocale(), []);
  const userId = user?.id ?? null;

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await api.get<StatusNoticeListResponse>('/api/v1/status-notices', signal ? { signal } : {});
      if (signal?.aborted) return;
      setNotices(response.notices);
      setNow(Date.now());
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') return;
      // A failed poll must not blank a strip that is already on screen —
      // during an incident the banner is the one thing worth keeping.
    }
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void load(ctrl.signal);
    const timer = window.setInterval(() => void load(ctrl.signal), POLL_MS);
    return () => {
      ctrl.abort();
      window.clearInterval(timer);
    };
  }, [load]);

  const shown = visibleNotices(notices, now, userId);
  if (shown.length === 0) return null;

  return (
    <div className="border-b border-rule-strong bg-ground">
      {shown.map((notice, index) => (
        <div key={notice.id} className={cx(index > 0 && 'border-t border-rule-faint')}>
          <BannerRow
            notice={notice}
            locale={locale}
            now={now}
            onDismiss={(item) => {
              dismissNotice(item, Date.now(), userId);
              setNow(Date.now());
            }}
          />
        </div>
      ))}
    </div>
  );
}
